import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Row, Col, DatePicker, Button } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { COUNTRIES } from "../../utils/countries";

const isValueEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === "object" && Object.keys(value).length === 0) return true;
  return false;
};
const isBase64Image = (value) => {
  return (
    typeof value === "string" &&
    (value.startsWith("data:image/") || // complete base64 with MIME type
      value.startsWith("/9j/") || // raw JPEG
      value.startsWith("iVBOR") || // raw PNG
      value.startsWith("R0lGOD")) // raw GIF
  );
};
const renderData = (data, parentKey = "") => {
  return Object.entries(data).map(([key, value]) => {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (isValueEmpty(value)) return null;

    if (typeof value === "object" && !Array.isArray(value)) {
      return (
        <div key={fullKey} className="ml-4 mb-2">
          <strong>{key}:</strong>
          <div className="ml-4">{renderData(value, fullKey)}</div>
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div key={fullKey} className="ml-4 mb-2">
          <strong>{key}:</strong>
          <ul className="list-disc list-inside ml-4">
            {value.map((item, index) =>
              isValueEmpty(item) ? null : (
                <li key={`${fullKey}[${index}]`}>
                  {typeof item === "object"
                    ? renderData(item, `${fullKey}[${index}]`)
                    : `${item}`}
                </li>
              )
            )}
          </ul>
        </div>
      );
    }

    // 🔍 Intelligent image detection
    if (isBase64Image(value)) {
      const src = value.startsWith("data:image/")
        ? value
        : `data:image/jpeg;base64,${value}`; // default to jpeg if missing MIME

      return (
        <div key={fullKey} className="ml-4 mb-2">
          <strong>{key}:</strong>
          <div className="mt-1">
            <img
              src={src}
              alt={key}
              className="max-w-xs max-h-60 border rounded"
            />
          </div>
        </div>
      );
    }

    return (
      <div key={fullKey} className="ml-4 mb-1">
        <strong>{key}:</strong> {value?.toString()}
      </div>
    );
  });
};


export const NewApplicantFinalReviewPage = ({
  studentBasicInfo,
  parent1BasicInfo,
  parent2BasicInfo,
  studentInfo,
  parent1Info,
  parent2Info,
  studentHealthInfo,
  healthIssue,
  StudentAcadamicHistory,
}) => {
  const finalData = {
    studentBasicInfo,
    parent1BasicInfo,
    parent2BasicInfo,
    studentInfo,
    parent1Info,
    parent2Info,
    studentHealthInfo,
    healthIssue,
    StudentAcadamicHistory,
  };

  return (
    <div className="p-6 bg-white rounded-md shadow">
      <h2 className="text-2xl font-bold mb-4">Final Review</h2>
      <div className="space-y-4">{renderData(finalData)}</div>
    </div>
  );
};
export const ExistingApplicantFinalReviewPage = ({
  studentBasicInfo,
  studentInfo,
  studentHealthInfo,
  healthIssue,
  StudentAcadamicHistory,
}) => {
  const finalData = {
    studentBasicInfo,
    studentInfo,
    studentHealthInfo,
    healthIssue,
    StudentAcadamicHistory,
  };

  return (
    <div className="p-6 bg-white rounded-md shadow">
      <h2 className="text-2xl font-bold mb-4">Final Review</h2>
      <div className="space-y-4">{renderData(finalData)}</div>
    </div>
  );
};

export const BasicInformation = ({
  formik,
  handleFieldChange,
  parentTypeOptions,
  genderOptions,
  phoneTypeOptions,
  relationshipToStudentOptions,
  occupationTypeOptions,
  selectStyle,
  inputStyle,
  errorStyle,
  currentStep,
  title,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="mb-4">
          <label
            htmlFor="firstName"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            readOnly={currentStep === 1}
            value={formik.values.firstName || ""}
            placeholder={formik.values.firstName || "First Name"}
            onChange={handleFieldChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="surName"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Surname
          </label>
          <input
            type="text"
            id="surName"
            name="surName"
            readOnly={currentStep === 1}
            value={formik.values.surName || ""}
            placeholder={formik.values.surName || "Surname"}
            onChange={handleFieldChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="lastName"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            readOnly={currentStep === 1}
            value={formik.values.lastName || ""}
            placeholder={formik.values.lastName || "Last Name"}
            onChange={handleFieldChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            readOnly={currentStep === 1}
            value={formik.values.email || ""}
            placeholder={formik.values.email || "Email"}
            onChange={handleFieldChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>
        <div>
          <label
            htmlFor="gender"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            id="gender"
            name="gender"
            value={formik.values.gender}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            disabled={currentStep === 1}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          >
            <option value="">Select gender</option>
            {genderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formik.touched.gender && formik.errors.gender && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.gender}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="phoneNumber"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formik.values.phoneNumber || ""}
            readOnly={currentStep === 1}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <div className="text-red-500 text-xs mt-1">
              {formik.errors.phoneNumber}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="dob"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="dob"
            name="dob"
            value={formik.values.dob || ""}
            readOnly={currentStep === 1}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
          {formik.touched.dob && formik.errors.dob && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.dob}</div>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="nationality"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Nationality <span className="text-red-500">*</span>
          </label>
          <select
            name="nationality"
            value={formik.values.nationality}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            disabled={currentStep === 1}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              currentStep === 1 ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          >
            <option value="">Select Birth Nationality *</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          {formik.touched.nationality && formik.errors.nationality && (
            <div className="text-red-500 text-xs mt-1">
              {formik.errors.nationality}
            </div>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="birthCountry"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Birth Country <span className="text-red-500">*</span>
          </label>
          <select
            name="birthCountry"
            value={formik.values.birthCountry}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            className={`${selectStyle} ${
              formik.touched.birthCountry && formik.errors.birthCountry
                ? errorStyle
                : ""
            }`}
          >
            <option value="">Select Birth Country *</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          {formik.touched.birthCountry && formik.errors.birthCountry && (
            <div className="text-red-500 text-xs mt-1">
              {formik.errors.birthCountry}
            </div>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="photo"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Passport size Photo <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            id="photo"
            name="photo"
            accept=".jpg,.jpeg,.png"
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              console.log("Selected Photo File:", file);

              if (file) {
                // Check file size before conversion
                if (file.size > 10 * 1024 * 1024) {
                  // Show a warning for large files
                  alert(`Warning: The file is large (${(
                    file.size /
                    1024 /
                    1024
                  ).toFixed(2)} MB). 
                            This may cause slow upload or submission. Consider compressing the file.`);
                }

                // Convert file to base64
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64String = reader.result.split(",")[1];
                  console.log("Base64 String Length:", base64String.length);

                  // Create an object that includes file metadata
                  const fileData = {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    base64: base64String,
                  };

                  formik.setFieldValue("photo", fileData);
                };
                reader.readAsDataURL(file);
              }
            }}
            onBlur={formik.handleBlur}
            className={`${inputStyle} ${
              formik.touched.photo && formik.errors.photo ? errorStyle : ""
            }`}
          />
          {formik.touched.photo && formik.errors.photo && (
            <div className="text-red-500 text-xs mt-1">
              {formik.errors.photo}
            </div>
          )}
          <div className="text-gray-600 text-xs mt-1">
            Supported types: JPG, PNG. Max file size: 10MB
          </div>
        </div>
      </div>
    </div>
  );
};

// StudentInformation.js

export const StudentInformation = ({
  formik,
  handleFieldChange,
  selectStyle,
  inputStyle,
  errorStyle,
  religionOptions,
}) => {
  return (                                                                           
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">
        Student Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="primaryLanguage"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Primary Language
          </label>
          <input
            type="text"
            id="primaryLanguage"
            name="primaryLanguage"
            placeholder={formik.values.primaryLanguage || "Primary Language"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.primaryLanguage}
            className={`${inputStyle} ${
              formik.touched.primaryLanguage && formik.errors.primaryLanguage
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.primaryLanguage && formik.errors.primaryLanguage && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.primaryLanguage}
            </div>
          )}
        </div>
        <div>
          <label
            htmlFor="otherLanguages"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Other Languages
          </label>
          <input
            type="text"
            id="otherLanguages"
            name="otherLanguages"
            placeholder={formik.values.otherLanguages || "Other Languages"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.otherLanguages}
            className={inputStyle}
          />
        </div>
        <div>
          <label
            htmlFor="religion"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Religion
          </label>
          <select
            id="religion"
            name="religion"
            value={formik.values.religion}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            className={`${selectStyle} ${
              formik.touched.religion && formik.errors.religion
                ? errorStyle
                : ""
            }`}
          >
            <option value="">Select religion</option>
            {religionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formik.touched.religion && formik.errors.religion && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.religion}
            </div>
          )}
        </div>
        <div>
          <label
            htmlFor="birthCertificateFile"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Birth Certificate File
          </label>
          <input
            type="file"
            id="birthCertificateFile"
            name="birthCertificateFile"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  const base64String = reader.result.split(",")[1];
                  formik.setFieldValue("birthCertificateFile", base64String);
                };
                reader.readAsDataURL(file);
              }
            }}
            onBlur={formik.handleBlur}
            className={`${inputStyle} ${
              formik.touched.birthCertificateFile &&
              formik.errors.birthCertificateFile
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.birthCertificateFile &&
            formik.errors.birthCertificateFile && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.birthCertificateFile}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export const ParentAdditionalInformation = ({
  formik,
  handleFieldChange,
  genderOptions,
  phoneTypeOptions,
  occupationTypeOptions,
  relationshipToStudentOptions,
  inputStyle,
  selectStyle,
  errorStyle,
  whoIs,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">
        {whoIs}'s Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Work Place */}
        <div>
          <label
            htmlFor="parentWorkPlace"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Work Place <span className="text-red-500">*</span>
          </label>
          <input
            id="parentWorkPlace"
            type="text"
            name="parentWorkPlace"
            placeholder={`Enter ${whoIs}'s work place`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentWorkPlace}
            className={`${inputStyle} ${
              formik.touched.parentWorkPlace && formik.errors.parentWorkPlace
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentWorkPlace && formik.errors.parentWorkPlace && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentWorkPlace}
            </div>
          )}
        </div>
        {/* Work Position */}
        <div>
          <label
            htmlFor="parentWorkPosition"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Work Position <span className="text-red-500">*</span>
          </label>
          <input
            id="parentWorkPosition"
            type="text"
            name="parentWorkPosition"
            placeholder={`Enter ${whoIs}'s work position`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentWorkPosition}
            className={`${inputStyle} ${
              formik.touched.parentWorkPosition &&
              formik.errors.parentWorkPosition
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentWorkPosition &&
            formik.errors.parentWorkPosition && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentWorkPosition}
              </div>
            )}
        </div>
        {/* Address */}
        <div>
          <label
            htmlFor="parentAddress"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Address <span className="text-red-500">*</span>
          </label>
          <input
            id="parentAddress"
            type="text"
            name="parentAddress"
            placeholder={`Enter ${whoIs}'s address`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentAddress}
            className={`${inputStyle} ${
              formik.touched.parentAddress && formik.errors.parentAddress
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentAddress && formik.errors.parentAddress && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentAddress}
            </div>
          )}
        </div>
        {/* P.O. Box */}
        <div>
          <label
            htmlFor="parentPoBox"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s P.O. Box
          </label>
          <input
            id="parentPoBox"
            type="text"
            name="parentPoBox"
            placeholder={`Enter ${whoIs}'s P.O. Box`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentPoBox}
            className={`${inputStyle} ${
              formik.touched.parentPoBox && formik.errors.parentPoBox
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentPoBox && formik.errors.parentPoBox && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentPoBox}
            </div>
          )}
        </div>
        {/* Sub City */}
        <div>
          <label
            htmlFor="parentSubCity"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Sub City <span className="text-red-500">*</span>
          </label>
          <input
            id="parentSubCity"
            type="text"
            name="parentSubCity"
            placeholder={`Enter ${whoIs}'s sub city`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentSubCity}
            className={`${inputStyle} ${
              formik.touched.parentSubCity && formik.errors.parentSubCity
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentSubCity && formik.errors.parentSubCity && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentSubCity}
            </div>
          )}
        </div>
        {/* Kebele */}
        <div>
          <label
            htmlFor="parentKebele"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Kebele <span className="text-red-500">*</span>
          </label>
          <input
            id="parentKebele"
            type="text"
            name="parentKebele"
            placeholder={`Enter ${whoIs}'s kebele`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentKebele}
            className={`${inputStyle} ${
              formik.touched.parentKebele && formik.errors.parentKebele
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentKebele && formik.errors.parentKebele && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentKebele}
            </div>
          )}
        </div>
        {/* House Number */}
        <div>
          <label
            htmlFor="parentHouseNumber"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s House Number <span className="text-red-500">*</span>
          </label>
          <input
            id="parentHouseNumber"
            type="text"
            name="parentHouseNumber"
            placeholder={`Enter ${whoIs}'s house number`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentHouseNumber}
            className={`${inputStyle} ${
              formik.touched.parentHouseNumber &&
              formik.errors.parentHouseNumber
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentHouseNumber &&
            formik.errors.parentHouseNumber && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentHouseNumber}
              </div>
            )}
        </div>
        {/* Mobile Phone */}
        <div>
          <label
            htmlFor="parentMobilePhone"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Mobile Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="parentMobilePhone"
            type="tel"
            name="parentMobilePhone"
            placeholder={`Enter ${whoIs}'s mobile phone`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentMobilePhone}
            className={`${inputStyle} ${
              formik.touched.parentMobilePhone &&
              formik.errors.parentMobilePhone
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentMobilePhone &&
            formik.errors.parentMobilePhone && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentMobilePhone}
              </div>
            )}
        </div>
        {/* Email */}
        <div>
          <label
            htmlFor="parentEmail"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Email <span className="text-red-500">*</span>
          </label>
          <input
            id="parentEmail"
            type="email"
            name="parentEmail"
            placeholder={`Enter ${whoIs}'s email`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentEmail}
            className={`${inputStyle} ${
              formik.touched.parentEmail && formik.errors.parentEmail
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentEmail && formik.errors.parentEmail && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentEmail}
            </div>
          )}
        </div>
        {/* parentOccupationType */}
        <div>
          <label
            htmlFor="parentOccupationType"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Occupation Type <span className="text-red-500">*</span>
          </label>
          <select
            id="parentOccupationType"
            name="parentOccupationType"
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentOccupationType}
            className={`${inputStyle} ${
              formik.touched.parentOccupationType &&
              formik.errors.parentOccupationType
                ? errorStyle
                : ""
            }`}
          >
            <option value="">Select {whoIs}'s Occupation Type</option>
            {occupationTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formik.touched.parentOccupationType &&
            formik.errors.parentOccupationType && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentOccupationType}
              </div>
            )}
        </div>
        {/* parentHomePhone */}
        <div>
          <label
            htmlFor="parentHomePhone"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Home Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="parentHomePhone"
            type="text"
            name="parentHomePhone"
            placeholder={`Enter ${whoIs}'s Occupation Type`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentHomePhone}
            className={`${inputStyle} ${
              formik.touched.parentHomePhone && formik.errors.parentHomePhone
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentHomePhone && formik.errors.parentHomePhone && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentHomePhone}
            </div>
          )}
        </div>
        {/* parentOfficePhone */}
        <div>
          <label
            htmlFor="parentOfficePhone"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Office Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="parentOfficePhone"
            type="text"
            name="parentOfficePhone"
            placeholder={`Enter ${whoIs}'s Occupation Type`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentOfficePhone}
            className={`${inputStyle} ${
              formik.touched.parentOfficePhone &&
              formik.errors.parentOfficePhone
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentOfficePhone &&
            formik.errors.parentOfficePhone && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentOfficePhone}
              </div>
            )}
        </div>
        {/* parentOtherPhone */}
        <div>
          <label
            htmlFor="parentOtherPhone"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Other Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="parentOtherPhone"
            type="text"
            name="parentOtherPhone"
            placeholder={`Enter ${whoIs}'s Occupation Type`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentOtherPhone}
            className={`${inputStyle} ${
              formik.touched.parentOtherPhone && formik.errors.parentOtherPhone
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentOtherPhone &&
            formik.errors.parentOtherPhone && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentOtherPhone}
              </div>
            )}
        </div>
        {/* parentFaxNumber */}
        <div>
          <label
            htmlFor="parentFaxNumber"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Fax Number <span className="text-red-500">*</span>
          </label>
          <input
            id="parentFaxNumber"
            type="text"
            name="parentFaxNumber"
            placeholder={`Enter ${whoIs}'s Occupation Type`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentFaxNumber}
            className={`${inputStyle} ${
              formik.touched.parentFaxNumber && formik.errors.parentFaxNumber
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.parentFaxNumber && formik.errors.parentFaxNumber && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.parentFaxNumber}
            </div>
          )}
        </div>
        {/* parentPreferredPhoneType */}
        <div>
          <label
            htmlFor="parentPreferredPhoneType"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Preferred Phone Type{" "}
            <span className="text-red-500">*</span>
          </label>
          <select
            id="parentPreferredPhoneType"
            name="parentPreferredPhoneType"
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.parentPreferredPhoneType}
            className={`${inputStyle} ${
              formik.touched.parentPreferredPhoneType &&
              formik.errors.parentPreferredPhoneType
                ? errorStyle
                : ""
            }`}
          >
            <option value="">Select {whoIs}'s prefered phone Type</option>
            {phoneTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formik.touched.parentPreferredPhoneType &&
            formik.errors.parentPreferredPhoneType && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.parentPreferredPhoneType}
              </div>
            )}
        </div>
        <div>
          <label
            htmlFor="idNumber"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s ID Number <span className="text-red-500">*</span>
          </label>
          <input
            id="idNumber"
            type="text"
            name="idNumber"
            placeholder={`Enter ${whoIs}'s Occupation Type`}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.idNumber}
            className={`${inputStyle} ${
              formik.touched.idNumber && formik.errors.idNumber
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.idNumber && formik.errors.idNumber && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.idNumber}
            </div>
          )}
        </div>
        {/* idCopyFile */}
        <div className="mb-4">
          <label
            htmlFor="idCopyFile"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            {whoIs}'s Identification card{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            id="idCopyFile"
            name="idCopyFile"
            accept=".jpg,.jpeg,.png"
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              console.log("Selected File:", file);

              if (file) {
                // Check file size before conversion
                if (file.size > 10 * 1024 * 1024) {
                  // Show a warning for large files
                  alert(`Warning: The file is large (${(
                    file.size /
                    1024 /
                    1024
                  ).toFixed(2)} MB). 
                            This may cause slow upload or submission. Consider compressing the file.`);
                }

                // Convert file to base64
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64String = reader.result.split(",")[1];
                  console.log("Base64 String Length:", base64String.length);

                  // Create an object that includes file metadata
                  const fileData = {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    base64: base64String,
                  };

                  formik.setFieldValue("idCopyFile", fileData);
                };
                reader.readAsDataURL(file);
              }
            }}
            onBlur={formik.handleBlur}
            className={`${inputStyle} ${
              formik.touched.photo && formik.errors.photo ? errorStyle : ""
            }`}
          />
          {formik.touched.photo && formik.errors.photo && (
            <div className="text-red-500 text-xs mt-1">
              {formik.errors.photo}
            </div>
          )}
          <div className="text-gray-600 text-xs mt-1">
            Supported types: JPG, PNG. Max file size: 10MB
          </div>
        </div>
      </div>
    </div>
  );
};
export const StudentHistoryForm = ({
  formik,
  handleFieldChange,
  selectStyle,
  inputStyle,
  errorStyle,
  isAcademicRequired,
  setIsAcademicRequired,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">
        Student Historical Information
      </h3>
      <div className="mb-4">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={isAcademicRequired}
            onChange={(e) => setIsAcademicRequired(e.target.checked)}
            className="mr-2"
          />
          Is academic history required?
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="primaryLanguage"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            School Name
          </label>
          <input
            type="text"
            id="schoolName"
            name="schoolName"
            disabled={!isAcademicRequired}
            placeholder={"Previous School Name"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.schoolName}
            className={`${inputStyle} ${
              formik.touched.schoolName && formik.errors.schoolName
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.schoolName && formik.errors.schoolName && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.schoolName}
            </div>
          )}
        </div>
        <div>
          <label
            htmlFor="gradeFrom"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Grade From
          </label>
          <input
            type="number"
            id="gradeFrom"
            name="gradeFrom"
            disabled={!isAcademicRequired}
            placeholder={"grade From"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.gradeFrom}
            className={`${inputStyle} ${
              formik.touched.gradeFrom && formik.errors.gradeFrom
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.gradeFrom && formik.errors.gradeFrom && (
            <div className="error">{formik.errors.gradeFrom}</div>
          )}
        </div>
        <div>
          <label
            htmlFor="gradeTo "
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Grade To
          </label>
          <input
            type="number"
            id="gradeTo"
            name="gradeTo"
            disabled={!isAcademicRequired}
            placeholder={"grade To"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.gradeTo}
            className={`${inputStyle} ${
              formik.touched.gradeTo && formik.errors.gradeTo ? errorStyle : ""
            }`}
          />
          {formik.touched.gradeTo && formik.errors.gradeTo && (
            <div className="error">{formik.errors.gradeTo}</div>
          )}
        </div>
        <div>
          <label
            htmlFor="schoolPhoneNumber "
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Entry Period
          </label>
          <DatePicker
            style={{ width: "100%" }}
            id="entryDate"
            name="entryDate"
            disabled={!isAcademicRequired}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            value={
              formik.values.entryDate ? dayjs(formik.values.entryDate) : null
            }
            onChange={(date) =>
              formik.setFieldValue("entryDate", date ? date.toISOString() : "")
            }
            onBlur={() => formik.setFieldTouched("entryDate", true)}
            className={`${inputStyle} ${
              formik.touched.entryDate && formik.errors.entryDate
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.entryDate && formik.errors.entryDate && (
            <div className="error">{formik.errors.entryDate}</div>
          )}
        </div>
        <div>
          <label
            htmlFor="schoolPhoneNumber "
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Exit Period
          </label>
          <DatePicker
            style={{ width: "100%" }}
            id="exitDate"
            name="exitDate"
            disabled={!isAcademicRequired}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            value={
              formik.values.exitDate ? dayjs(formik.values.exitDate) : null
            }
            onChange={(date) =>
              formik.setFieldValue("exitDate", date ? date.toISOString() : "")
            }
            onBlur={() => formik.setFieldTouched("exitDate", true)}
            className={`${inputStyle} ${
              formik.touched.exitDate && formik.errors.exitDate
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.exitDate && formik.errors.exitDate && (
            <div className="error">{formik.errors.exitDate}</div>
          )}
        </div>
        <div>
          <label
            htmlFor="schoolAddress "
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            School Address
          </label>
          <input
            type="text"
            id="schoolAddress"
            name="schoolAddress"
            disabled={!isAcademicRequired}
            placeholder={"School Address"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.schoolAddress}
            className={`${inputStyle} ${
              formik.touched.schoolAddress && formik.errors.schoolAddress
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.schoolAddress && formik.errors.schoolAddress && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.schoolAddress}
            </div>
          )}
        </div>
        <div>
          <label
            htmlFor="schoolAddress "
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            school Phone Number
          </label>
          <input
            type="text"
            id="schoolPhoneNumber"
            name="schoolPhoneNumber"
            disabled={!isAcademicRequired}
            placeholder={"school Phone Number"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.schoolPhoneNumber}
            className={`${inputStyle} ${
              formik.touched.schoolPhoneNumber &&
              formik.errors.schoolPhoneNumber
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.schoolPhoneNumber &&
            formik.errors.schoolPhoneNumber && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.schoolPhoneNumber}
              </div>
            )}
        </div>
        <div>
          <label
            htmlFor="document"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Certificate File
          </label>
          <input
            type="file"
            id="document"
            name="document"
            disabled={!isAcademicRequired}
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  const base64String = reader.result.split(",")[1];
                  formik.setFieldValue("document", base64String);
                };
                reader.readAsDataURL(file);
              }
            }}
            onBlur={formik.handleBlur}
            className={`${inputStyle} ${
              formik.touched.document && formik.errors.document
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.document && formik.errors.document && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.document}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export const HealthInformationForm = ({
  formik,
  handleFieldChange,
  inputStyle,
  errorStyle,
  selectStyle,
  isHealthCheckMandatory,
  setIsHealthCheckMandatory,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">
        Health Information
      </h3>
      <div className="mb-4 bg-red-100 p-2">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={isHealthCheckMandatory}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsHealthCheckMandatory(checked);
              formik.validateForm(); // optional: force revalidation on toggle
            }}
            className="mr-2"
          />
          Is health information available?
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="referenceHospital"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Reference Hospital {isHealthCheckMandatory ? "true" : "false"}
          </label>
          <input
            type="text"
            id="referenceHospital"
            name="referenceHospital"
            disabled={!isHealthCheckMandatory}
            placeholder={
              formik.values.referenceHospital || "Reference Hospital"
            }
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.referenceHospital}
            className={`${inputStyle} ${
              formik.touched.referenceHospital &&
              formik.errors.referenceHospital
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.referenceHospital &&
            formik.errors.referenceHospital && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.referenceHospital}
              </div>
            )}
        </div>

        <div>
          <label
            htmlFor="doctorName"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Doctor Name
          </label>
          <input
            type="text"
            id="doctorName"
            name="doctorName"
            disabled={!isHealthCheckMandatory}
            placeholder={formik.values.doctorName || "Doctor Name"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.doctorName}
            className={`${inputStyle} ${
              formik.touched.doctorName && formik.errors.doctorName
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.doctorName && formik.errors.doctorName && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.doctorName}
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="hospitalNumber"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Hospital Number
          </label>
          <input
            type="text"
            id="hospitalNumber"
            name="hospitalNumber"
            disabled={!isHealthCheckMandatory}
            placeholder={formik.values.hospitalNumber || "Hospital Number"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.hospitalNumber}
            className={`${inputStyle} ${
              formik.touched.hospitalNumber && formik.errors.hospitalNumber
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.hospitalNumber && formik.errors.hospitalNumber && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.hospitalNumber}
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="doctorNumber"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Doctor Number
          </label>
          <input
            type="text"
            id="doctorNumber"
            name="doctorNumber"
            disabled={!isHealthCheckMandatory}
            placeholder={formik.values.doctorNumber || "Doctor Number"}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.doctorNumber}
            className={`${inputStyle} ${
              formik.touched.doctorNumber && formik.errors.doctorNumber
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.doctorNumber && formik.errors.doctorNumber && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.doctorNumber}
            </div>
          )}
        </div>

        <div className="col-span-2">
          <label
            htmlFor="healthDescription"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Health Description
          </label>
          <textarea
            id="healthDescription"
            name="healthDescription"
            disabled={!isHealthCheckMandatory}
            placeholder={
              formik.values.healthDescription || "Health Description"
            }
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.healthDescription}
            rows={4}
            className={`${inputStyle} h-32 ${
              formik.touched.healthDescription &&
              formik.errors.healthDescription
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.healthDescription &&
            formik.errors.healthDescription && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.healthDescription}
              </div>
            )}
        </div>

        <div>
          <label
            htmlFor="vaccinationStatus"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Vaccination Status
          </label>
          <select
            id="vaccinationStatus"
            name="vaccinationStatus"
            disabled={!isHealthCheckMandatory}
            value={formik.values.vaccinationStatus}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            className={`${selectStyle} ${
              formik.touched.vaccinationStatus &&
              formik.errors.vaccinationStatus
                ? errorStyle
                : ""
            }`}
          >
            <option value="">Select Vaccination Status *</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
          {formik.touched.vaccinationStatus &&
            formik.errors.vaccinationStatus && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.vaccinationStatus}
              </div>
            )}
        </div>

        <div>
          <label
            htmlFor="vaccinationExplanation"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            Vaccination Explanation
          </label>
          <textarea
            id="vaccinationExplanation"
            name="vaccinationExplanation"
            disabled={!isHealthCheckMandatory}
            placeholder={
              formik.values.vaccinationExplanation || "Vaccination Explanation"
            }
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            value={formik.values.vaccinationExplanation}
            className={`${inputStyle} h-32 ${
              formik.touched.vaccinationExplanation &&
              formik.errors.vaccinationExplanation
                ? errorStyle
                : ""
            }`}
          />
          {formik.touched.vaccinationExplanation &&
            formik.errors.vaccinationExplanation && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.vaccinationExplanation}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
export const HealthIssueForm = ({
  formik,
  handleFieldChange,
  newHealthIssue,
  setNewHealthIssue,
  selectedIssueType,
  setSelectedIssueType,
  inputStyle,
  errorStyle,
  selectStyle,
  isHealthIssueRequired,
  setIsHealthIssueRequired,
}) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="col-span-2">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Health Issues
            </label>
            <div className="mb-4 bg-red-100 p-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={isHealthIssueRequired}
                  onChange={(e) => setIsHealthIssueRequired(e.target.checked)}
                  className="mr-2"
                />
                Is there any health issue?
              </label>
            </div>

            {/* Render each health issue object */}
            <div className="flex flex-wrap gap-2">
              {formik.values.healthIssues?.map((issue, index) => (
                <div
                  key={index}
                  className="flex items-center bg-gray-100 rounded-md px-3 py-1"
                >
                  <span className="mr-2 font-medium">{issue.issueName}</span>
                  <span className="text-sm text-gray-500">
                    ({issue.issueType})
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newIssues = [...formik.values.healthIssues];
                      newIssues.splice(index, 1);
                      formik.setFieldValue("healthIssues", newIssues);
                    }}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/* Issue Type Select */}
            <div>
              <label
                htmlFor="issueType"
                className="block text-gray-700 text-sm font-bold mb-2"
              >
                Issue Type <span className="text-red-500">*</span>
              </label>
              <select
                id="issueType"
                name="issueType"
                value={selectedIssueType}
                disabled={!isHealthIssueRequired}
                onChange={(e) => setSelectedIssueType(e.target.value)}
                className={`${selectStyle}`}
              >
                <option value="">Select issue Type</option>
                <option value="Neurological">Neurological</option>
                <option value="Allergy">Allergy</option>
                <option value="Chronic">Chronic</option>
                <option value="Respiratory">Respiratory</option>
                <option value="Gastrointestinal">Gastrointestinal</option>
              </select>
            </div>

            {/* Input + Add Button */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add a health issue"
                value={newHealthIssue}
                disabled={!isHealthIssueRequired}
                onChange={(e) => setNewHealthIssue(e.target.value)}
                className={inputStyle}
              />
              <button
                type="button"
                disabled={!isHealthIssueRequired}
                onClick={() => {
                  if (newHealthIssue.trim() && selectedIssueType.trim()) {
                    const currentIssues = formik.values.healthIssues || [];
                    formik.setFieldValue("healthIssues", [
                      ...currentIssues,
                      {
                        issueName: newHealthIssue.trim(),
                        issueType: selectedIssueType.trim(),
                      },
                    ]);
                    setNewHealthIssue("");
                    setSelectedIssueType("");
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default {
  BasicInformation,
  StudentInformation,
  ParentAdditionalInformation,
  StudentHistoryForm,
  HealthInformationForm,
  HealthIssueForm,
};
